import { useCallback, useEffect, useRef, useState } from "react";
import { ReCaptchaProvider, ReCaptchaV2, ReCaptchaV3 } from "react-recaptcha-x";
import {
  useRecaptchaTokenRefresh,
  useRecaptchaV2,
  useRecaptchaV3,
} from "../../../hooks/useRecaptcha";

interface FirstAccessRecaptchaProps {
  action: string;
  onTokenChange: (token: string, version: string) => void;
  onPendingChange?: (pending: boolean) => void;
  onInvalidRecaptcha?: () => void;
}

export function useFirstAccessRecaptcha({
  action,
  onTokenChange,
  onPendingChange,
}: Pick<
  FirstAccessRecaptchaProps,
  "action" | "onTokenChange" | "onPendingChange"
>) {
  const refreshRecaptchaV3Ref = useRef<(() => void) | undefined>();
  const [handleRecaptchaV3Callback, captchaV3Params] = useRecaptchaV3(
    refreshRecaptchaV3Ref
  );
  const [handleRecaptchaV2Callback, captchaV2Params] = useRecaptchaV2();
  const [enableRecaptchaV2, setEnableRecaptchaV2] = useState(false);

  useRecaptchaTokenRefresh(refreshRecaptchaV3Ref);

  useEffect(() => {
    if (!enableRecaptchaV2 && captchaV3Params.v3Token) {
      onTokenChange(captchaV3Params.v3Token, "3");
    }
  }, [captchaV3Params.v3Token, enableRecaptchaV2, onTokenChange]);

  useEffect(() => {
    if (enableRecaptchaV2 && captchaV2Params.v2Token) {
      onTokenChange(captchaV2Params.v2Token, "2");
    }
  }, [captchaV2Params.v2Token, enableRecaptchaV2, onTokenChange]);

  useEffect(() => {
    const pending = enableRecaptchaV2
      ? !captchaV2Params.v2Token
      : captchaV3Params.v3Retrieving || !captchaV3Params.v3Token;
    onPendingChange?.(pending);
  }, [
    captchaV2Params.v2Token,
    captchaV3Params.v3Retrieving,
    captchaV3Params.v3Token,
    enableRecaptchaV2,
    onPendingChange,
  ]);

  const handleInvalidRecaptcha = () => {
    if (!enableRecaptchaV2) {
      setEnableRecaptchaV2(true);
    }
  };

  const refreshRecaptcha = useCallback(() => {
    if (!enableRecaptchaV2) {
      refreshRecaptchaV3Ref.current?.();
    }
  }, [enableRecaptchaV2]);

  const isReady = enableRecaptchaV2
    ? Boolean(captchaV2Params.v2Token)
    : Boolean(captchaV3Params.v3Token) && !captchaV3Params.v3Retrieving;

  const recaptchaField = (
    <ReCaptchaProvider
      siteKeyV2={process.env.REACT_APP_RECAPTCHA_V2_SITEKEY}
      siteKeyV3={process.env.REACT_APP_RECAPTCHA_V3_SITEKEY}
    >
      {!enableRecaptchaV2 && (
        <ReCaptchaV3 callback={handleRecaptchaV3Callback} action={action} />
      )}
      {enableRecaptchaV2 && (
        <ReCaptchaV2 callback={handleRecaptchaV2Callback} className="mt-4" />
      )}
    </ReCaptchaProvider>
  );

  return {
    recaptchaField,
    isReady,
    handleInvalidRecaptcha,
    refreshRecaptcha,
    enableRecaptchaV2,
  };
}
